import { useEffect, useMemo, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import {
  fetchComplianceRules,
  createComplianceRule,
  updateComplianceRule,
  deleteComplianceRule,
  fetchComplianceTerms,
  createComplianceTerm,
  deleteComplianceTerm,
  fetchComplianceTermMatches,
  fetchApprovedMessages,
  createApprovedMessage,
  updateApprovedMessage,
  deleteApprovedMessage,
  fetchAuditLog,
  type ComplianceRule,
  type ComplianceSeverity,
  type ComplianceTerm,
  type ComplianceTermCategory,
  type ComplianceTermMatch,
  type ApprovedMessage,
  type AuditLogEntry,
} from "@/services/admin";

const severityLabels: Record<ComplianceSeverity, string> = {
  info: "Informativa",
  warning: "Advertencia",
  critical: "Crítica",
};
const severityStyles: Record<ComplianceSeverity, string> = {
  info: "bg-background-200 text-foreground-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const categoryLabels: Record<ComplianceTermCategory, string> = {
  prohibida: "Prohibida",
  restringida: "Restringida",
  observacion: "En observación",
};
const categoryStyles: Record<ComplianceTermCategory, string> = {
  prohibida: "bg-red-100 text-red-700",
  restringida: "bg-amber-100 text-amber-700",
  observacion: "bg-background-200 text-foreground-700",
};

const auditEntities = ["compliance_rules", "compliance_terms", "approved_messages"];

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `Hace ${days} d`;
}

type RuleForm = { title: string; description: string; severity: ComplianceSeverity };
type TermForm = { phrase: string; category: ComplianceTermCategory };
type MessageForm = { title: string; category: string; content: string };

const emptyRuleForm: RuleForm = { title: "", description: "", severity: "info" };
const emptyTermForm: TermForm = { phrase: "", category: "observacion" };
const emptyMessageForm: MessageForm = { title: "", category: "general", content: "" };

export default function AdminCumplimiento() {
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [terms, setTerms] = useState<ComplianceTerm[]>([]);
  const [matches, setMatches] = useState<ComplianceTermMatch[]>([]);
  const [messages, setMessages] = useState<ApprovedMessage[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [ruleModal, setRuleModal] = useState<{ editing: ComplianceRule | null } | null>(null);
  const [ruleForm, setRuleForm] = useState<RuleForm>(emptyRuleForm);
  const [termModal, setTermModal] = useState(false);
  const [termForm, setTermForm] = useState<TermForm>(emptyTermForm);
  const [messageModal, setMessageModal] = useState<{ editing: ApprovedMessage | null } | null>(null);
  const [messageForm, setMessageForm] = useState<MessageForm>(emptyMessageForm);
  const [saving, setSaving] = useState(false);

  const loadAll = () =>
    Promise.all([
      fetchComplianceRules(),
      fetchComplianceTerms(),
      fetchComplianceTermMatches(),
      fetchApprovedMessages(),
      fetchAuditLog(40),
    ]).then(([r, t, m, msgs, log]) => {
      setRules(r);
      setTerms(t);
      setMatches(m);
      setMessages(msgs);
      setAuditLog(log.filter((e) => auditEntities.includes(e.entity)));
    });

  useEffect(() => {
    let active = true;
    loadAll()
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : "No se pudo cargar cumplimiento.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2400);
  };

  const refreshQuiet = async () => {
    try {
      await loadAll();
    } catch {
      // la data en pantalla queda como estaba; no interrumpe al usuario
    }
  };

  const matchByTermId = useMemo(() => new Map(matches.map((m) => [m.termId, m.matches])), [matches]);

  // --- Reglas ---------------------------------------------------------
  const openNewRule = () => {
    setRuleForm(emptyRuleForm);
    setRuleModal({ editing: null });
  };
  const openEditRule = (rule: ComplianceRule) => {
    setRuleForm({ title: rule.title, description: rule.description, severity: rule.severity });
    setRuleModal({ editing: rule });
  };
  const submitRule = async () => {
    if (!ruleForm.title.trim()) return;
    setSaving(true);
    try {
      if (ruleModal?.editing) {
        await updateComplianceRule(ruleModal.editing.id, ruleForm);
        showToast("Regla actualizada.");
      } else {
        await createComplianceRule(ruleForm);
        showToast("Regla creada.");
      }
      setRuleModal(null);
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo guardar la regla.");
    } finally {
      setSaving(false);
    }
  };
  const toggleRule = async (rule: ComplianceRule) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
    try {
      await updateComplianceRule(rule.id, { active: !rule.active });
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo actualizar la regla.");
      await refreshQuiet();
    }
  };
  const removeRule = async (rule: ComplianceRule) => {
    if (!window.confirm(`¿Eliminar la regla "${rule.title}"?`)) return;
    try {
      await deleteComplianceRule(rule.id);
      showToast("Regla eliminada.");
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo eliminar la regla.");
    }
  };

  // --- Términos controlados --------------------------------------------
  const submitTerm = async () => {
    if (!termForm.phrase.trim()) return;
    setSaving(true);
    try {
      await createComplianceTerm(termForm);
      showToast("Término agregado.");
      setTermModal(false);
      setTermForm(emptyTermForm);
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo guardar el término.");
    } finally {
      setSaving(false);
    }
  };
  const removeTerm = async (term: ComplianceTerm) => {
    if (!window.confirm(`¿Eliminar "${term.phrase}" de los términos controlados?`)) return;
    try {
      await deleteComplianceTerm(term.id);
      showToast("Término eliminado.");
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo eliminar el término.");
    }
  };

  // --- Mensajes aprobados -----------------------------------------------
  const openNewMessage = () => {
    setMessageForm(emptyMessageForm);
    setMessageModal({ editing: null });
  };
  const openEditMessage = (msg: ApprovedMessage) => {
    setMessageForm({ title: msg.title, category: msg.category, content: msg.content });
    setMessageModal({ editing: msg });
  };
  const submitMessage = async () => {
    if (!messageForm.title.trim() || !messageForm.content.trim()) return;
    setSaving(true);
    try {
      if (messageModal?.editing) {
        await updateApprovedMessage(messageModal.editing.id, messageForm);
        showToast("Mensaje actualizado.");
      } else {
        await createApprovedMessage(messageForm);
        showToast("Mensaje creado.");
      }
      setMessageModal(null);
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo guardar el mensaje.");
    } finally {
      setSaving(false);
    }
  };
  const toggleMessage = async (msg: ApprovedMessage) => {
    setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, active: !m.active } : m)));
    try {
      await updateApprovedMessage(msg.id, { active: !msg.active });
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo actualizar el mensaje.");
      await refreshQuiet();
    }
  };
  const removeMessage = async (msg: ApprovedMessage) => {
    if (!window.confirm(`¿Eliminar el mensaje "${msg.title}"?`)) return;
    try {
      await deleteApprovedMessage(msg.id);
      showToast("Mensaje eliminado.");
      await refreshQuiet();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "No se pudo eliminar el mensaje.");
    }
  };

  return (
    <AdminShell title="Cumplimiento" description="Reglas, lenguaje controlado y biblioteca de mensajes aprobados del equipo.">
      {loading ? (
        <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
          <p className="mt-2">Cargando cumplimiento desde Supabase…</p>
        </div>
      ) : error ? (
        <div className="bg-background-100 border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error}</div>
      ) : (
        <div className="space-y-8">
          {/* Reglas de cumplimiento */}
          <section>
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-foreground-950">Reglas activas</h2>
              <button
                onClick={openNewRule}
                className="inline-flex items-center gap-1.5 bg-primary-500 text-background-950 rounded-full px-3.5 py-1.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Nueva regla
              </button>
            </div>
            <div className="mt-3 bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
              <ul className="divide-y divide-background-200">
                {rules.map((rule) => (
                  <li key={rule.id} className="p-4 flex items-start gap-3">
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${severityStyles[rule.severity]}`}>
                      {severityLabels[rule.severity]}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className={`font-heading font-bold ${rule.active ? "text-foreground-950" : "text-foreground-400 line-through"}`}>
                        {rule.title}
                      </div>
                      <div className="text-sm text-foreground-600">{rule.description}</div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => toggleRule(rule)}
                        title={rule.active ? "Desactivar" : "Activar"}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-200 cursor-pointer"
                      >
                        <i className={rule.active ? "ri-toggle-fill text-primary-600 text-lg" : "ri-toggle-line text-lg"}></i>
                      </button>
                      <button
                        onClick={() => openEditRule(rule)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-200 cursor-pointer"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        onClick={() => removeRule(rule)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-accent-600 hover:bg-background-200 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </li>
                ))}
                {rules.length === 0 ? <li className="p-8 text-center text-sm text-foreground-500">Todavía no hay reglas.</li> : null}
              </ul>
            </div>
          </section>

          {/* Palabras y frases controladas */}
          <section>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading font-bold text-foreground-950">Palabras y frases controladas</h2>
                <p className="text-xs text-foreground-500 mt-0.5">
                  Coincidencias reales dentro de los mensajes aprobados y las aperturas sugeridas a prospectos.
                </p>
              </div>
              <button
                onClick={() => setTermModal(true)}
                className="inline-flex items-center gap-1.5 bg-primary-500 text-background-950 rounded-full px-3.5 py-1.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Nuevo término
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {terms.map((term) => (
                <div key={term.id} className="bg-background-100 border border-background-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${categoryStyles[term.category]}`}>
                    {categoryLabels[term.category]}
                  </span>
                  <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">"{term.phrase}"</span>
                  <span className="text-xs font-bold text-foreground-700 flex-shrink-0">{matchByTermId.get(term.id) ?? 0} detecciones</span>
                  <button onClick={() => removeTerm(term)} className="text-foreground-400 hover:text-accent-600 cursor-pointer flex-shrink-0">
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              ))}
              {terms.length === 0 ? (
                <div className="sm:col-span-2 p-8 text-center text-sm text-foreground-500 bg-background-100 border border-background-200 rounded-xl">
                  Todavía no hay términos controlados.
                </div>
              ) : null}
            </div>
          </section>

          {/* Mensajes aprobados */}
          <section>
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-foreground-950">Biblioteca de mensajes aprobados</h2>
              <button
                onClick={openNewMessage}
                className="inline-flex items-center gap-1.5 bg-primary-500 text-background-950 rounded-full px-3.5 py-1.5 text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Nuevo mensaje
              </button>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
              {messages.map((msg) => (
                <div key={msg.id} className="bg-background-100 border border-background-200 rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="text-[10px] font-semibold bg-background-200 text-foreground-600 px-2 py-0.5 rounded-full">{msg.category}</span>
                      <div className={`font-heading font-bold mt-1 ${msg.active ? "text-foreground-950" : "text-foreground-400 line-through"}`}>
                        {msg.title}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => toggleMessage(msg)} className="w-7 h-7 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-200 cursor-pointer">
                        <i className={msg.active ? "ri-toggle-fill text-primary-600" : "ri-toggle-line"}></i>
                      </button>
                      <button onClick={() => openEditMessage(msg)} className="w-7 h-7 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-200 cursor-pointer">
                        <i className="ri-pencil-line text-sm"></i>
                      </button>
                      <button onClick={() => removeMessage(msg)} className="w-7 h-7 rounded-full flex items-center justify-center text-accent-600 hover:bg-background-200 cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 mt-2 line-clamp-3">{msg.content}</p>
                </div>
              ))}
              {messages.length === 0 ? (
                <div className="md:col-span-2 p-8 text-center text-sm text-foreground-500 bg-background-100 border border-background-200 rounded-xl">
                  Todavía no hay mensajes en la biblioteca.
                </div>
              ) : null}
            </div>
          </section>

          {/* Bitácora filtrada */}
          <section>
            <h2 className="font-heading font-bold text-foreground-950">Bitácora de cumplimiento</h2>
            <div className="mt-3 bg-background-100 border border-background-200 rounded-2xl p-5">
              {auditLog.length === 0 ? (
                <p className="text-sm text-foreground-500">Todavía no hay actividad registrada en esta sección.</p>
              ) : (
                <ul className="divide-y divide-background-200">
                  {auditLog.slice(0, 12).map((entry) => (
                    <li key={entry.id} className="py-2.5 flex items-start gap-3">
                      <i className="ri-shield-check-line text-foreground-400 mt-0.5"></i>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-foreground-800">{entry.summary}</div>
                        <div className="text-[11px] text-foreground-500">
                          {entry.actorName ?? "Sistema"} · {timeAgo(entry.createdAt)}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}

      {/* Modal: regla */}
      {ruleModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setRuleModal(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{ruleModal.editing ? "Editar regla" : "Nueva regla"}</h3>
            <div className="mt-4 space-y-3">
              <input
                value={ruleForm.title}
                onChange={(e) => setRuleForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Título"
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400"
              />
              <textarea
                value={ruleForm.description}
                onChange={(e) => setRuleForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Descripción"
                rows={3}
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400 resize-none"
              />
              <select
                value={ruleForm.severity}
                onChange={(e) => setRuleForm((f) => ({ ...f, severity: e.target.value as ComplianceSeverity }))}
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none cursor-pointer"
              >
                {(Object.keys(severityLabels) as ComplianceSeverity[]).map((s) => (
                  <option key={s} value={s}>
                    {severityLabels[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setRuleModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">
                Cancelar
              </button>
              <button
                onClick={submitRule}
                disabled={saving || !ruleForm.title.trim()}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal: término */}
      {termModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setTermModal(false)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">Nuevo término controlado</h3>
            <div className="mt-4 space-y-3">
              <input
                value={termForm.phrase}
                onChange={(e) => setTermForm((f) => ({ ...f, phrase: e.target.value }))}
                placeholder='Frase, ej. "sin riesgo"'
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400"
              />
              <select
                value={termForm.category}
                onChange={(e) => setTermForm((f) => ({ ...f, category: e.target.value as ComplianceTermCategory }))}
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none cursor-pointer"
              >
                {(Object.keys(categoryLabels) as ComplianceTermCategory[]).map((c) => (
                  <option key={c} value={c}>
                    {categoryLabels[c]}
                  </option>
                ))}
              </select>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setTermModal(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">
                Cancelar
              </button>
              <button
                onClick={submitTerm}
                disabled={saving || !termForm.phrase.trim()}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal: mensaje aprobado */}
      {messageModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-background-950/70 backdrop-blur-sm" onClick={() => setMessageModal(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-2xl p-5">
            <h3 className="font-heading font-bold text-foreground-950">{messageModal.editing ? "Editar mensaje" : "Nuevo mensaje aprobado"}</h3>
            <div className="mt-4 space-y-3">
              <input
                value={messageForm.title}
                onChange={(e) => setMessageForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Título"
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400"
              />
              <input
                value={messageForm.category}
                onChange={(e) => setMessageForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="Categoría, ej. prospección"
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400"
              />
              <textarea
                value={messageForm.content}
                onChange={(e) => setMessageForm((f) => ({ ...f, content: e.target.value }))}
                placeholder="Contenido del mensaje"
                rows={4}
                className="w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400 resize-none"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setMessageModal(null)} className="px-4 py-2 rounded-full text-sm font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer">
                Cancelar
              </button>
              <button
                onClick={submitMessage}
                disabled={saving || !messageForm.title.trim() || !messageForm.content.trim()}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
              >
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">
          {toast}
        </div>
      ) : null}
    </AdminShell>
  );
}
